import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  server,
} from "../config";

const ManageSubCategories = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Form fields
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMultiple, setIsMultiple] = useState(false);

  useEffect(() => {
    fetchSubCategories();
    fetchCategories();
  }, []);

  const fetchSubCategories = async () => {
    const res = await getRequest(`${server.uri}sub-categories`);
    if (res) {
      setSubCategories(res);
      setAllSubCategories(res);
    }
  };

  const fetchCategories = async () => {
    const res = await getRequest(`${server.uri}categories`);
    if (res) {
      setCategories(res);
    }
  };

  const resetForm = () => {
    setName("");
    setImageUrl("");
    setSelectedCategory(null);
    setIsMultiple(false);
  };

  const handleAddSubCategory = () => {
    resetForm();
    setEditingSubCategory(null);
    setOpenDialog(true);
  };

  const handleEditSubCategory = (subCat) => {
    setEditingSubCategory(subCat);
    setName(subCat.name || "");
    setImageUrl(subCat.imageUrl || "");
    setIsMultiple(subCat.isMultiple ?? false);
    const category = categories.find((c) => c.categoryId === subCat.categoryId);
    setSelectedCategory(category || null);
    setOpenDialog(true);
  };

  const handleDeleteSubCategory = async (id) => {
    if (window.confirm("Are you sure you want to delete this sub-category?")) {
      await deleteRequest(`${server.uri}sub-categories/${id}`);
      fetchSubCategories();
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSaveSubCategory = async () => {
    if (!selectedCategory) {
      alert("Please select a category");
      return;
    }

    const payload = {
      name,
      imageUrl,
      categoryId: selectedCategory.categoryId,
      isMultiple,
    };

    if (editingSubCategory) {
      await patchRequest(
        `${server.uri}sub-categories/${editingSubCategory.subCategoryId}`,
        payload
      );
    } else {
      await postRequest(`${server.uri}sub-categories`, payload);
    }

    fetchSubCategories();
    handleDialogClose();
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = allSubCategories.filter((sub) =>
      sub.name.toLowerCase().includes(value)
    );
    setSubCategories(filtered);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginTop: 8, marginBottom: 2 }}>
        Manage Sub-Categories
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search by sub-category name"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Search sx={{ color: "gray", marginRight: 1 }} />,
          }}
          sx={{ width: "60%" }}
        />
        <Button variant="contained" onClick={handleAddSubCategory}>
          Add Sub-Category
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Image</strong></TableCell>
                <TableCell><strong>Multiple?</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subCategories.map((sub) => (
                <TableRow key={sub.subCategoryId}>
                  <TableCell>{sub.name}</TableCell>
                  <TableCell>
                    {
                      categories.find((c) => c.categoryId === sub.categoryId)
                        ?.name || "—"
                    }
                  </TableCell>
                  <TableCell>
                    {sub.imageUrl ? (
                      <img
                        src={sub.imageUrl}
                        alt={sub.name}
                        style={{ width: 50, height: 50, objectFit: "cover" }}
                      />
                    ) : (
                      "No image"
                    )}
                  </TableCell>
                  <TableCell>{sub.isMultiple ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditSubCategory(sub)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteSubCategory(sub.subCategoryId)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {subCategories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No sub-categories found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSubCategory ? "Edit Sub-Category" : "Add New Sub-Category"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.name || ""}
            value={selectedCategory}
            onChange={(e, newValue) => setSelectedCategory(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select Category" variant="outlined" />
            )}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isMultiple}
                onChange={(e) => setIsMultiple(e.target.checked)}
              />
            }
            label="Is Multiple?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveSubCategory} variant="contained">
            {editingSubCategory ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageSubCategories;
